// TEMPORARY design-preview harness: renders the battery advice flow on
// synthetic data so the redesign can be checked without a login. Not routed
// in production; removed before release.
import { useMemo } from 'react'
import { useReportCache } from '@/stores/reportCache'
import { recommendInverterKw } from '@/lib/batterySizing'
import { hourlyNetPowerW, type HourlySlot } from '@/lib/energyHistory'
import type { BatteryReportData } from '@/hooks/useBatteryReport'
import { BatteryReport } from '@/features/battery/BatteryReport'

function buildSyntheticYear(): { slots: HourlySlot[]; priceEntries: Array<[string, number]> } {
  const slots: HourlySlot[] = []
  const priceEntries: Array<[string, number]> = []
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  start.setMonth(start.getMonth() - 12)

  for (let day = 0; day < 365; day++) {
    const season = Math.cos(((day - 172) / 365) * 2 * Math.PI) // 1 mid-summer
    for (let hour = 0; hour < 24; hour++) {
      const t = new Date(start.getTime() + (day * 24 + hour) * 3_600_000)
      const iso = new Date(Date.UTC(t.getFullYear(), t.getMonth(), t.getDate(), t.getHours())).toISOString()

      const eveningPeak = hour >= 17 && hour <= 22 ? 1100 : 0
      const daytime = hour >= 7 && hour <= 23 ? 250 : 0
      const consumptionWh = 220 + daytime + eveningPeak + Math.random() * 120

      const sun = Math.max(0, Math.sin(((hour - 6.5) / 11) * Math.PI))
      const peakW = 900 + 2300 * Math.max(0, (season + 1) / 2)
      const productionWh = sun * peakW * (0.7 + Math.random() * 0.3)

      slots.push({
        time: iso,
        delivered: Math.max(0, consumptionWh - productionWh),
        returned: Math.max(0, productionWh - consumptionWh),
        produced: productionWh,
      })

      const middayDip = hour >= 10 && hour <= 15 ? 0.09 : 0
      const eveningSpike = hour >= 17 && hour <= 20 ? 0.08 : 0
      priceEntries.push([iso, 0.24 - middayDip + eveningSpike + Math.random() * 0.03])
    }
  }
  return { slots, priceEntries }
}

export function DesignPreviewPage({ fresh = false }: { fresh?: boolean }) {
  const addressUuid = fresh ? 'preview-fresh' : 'preview'

  useMemo(() => {
    if (fresh) return
    const { slots, priceEntries } = buildSyntheticYear()
    const to = new Date()
    to.setHours(0, 0, 0, 0)
    const from = new Date(to)
    from.setMonth(from.getMonth() - 12)
    const data: BatteryReportData = {
      slots,
      inverterKw: recommendInverterKw(hourlyNetPowerW(slots)),
      priceEntries,
      periodDays: 365,
      hasSmartMeter: true,
      hasSolar: true,
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
    }
    useReportCache.getState().setEntry<BatteryReportData>(`batteryAdvice:${addressUuid}:12`, data, new Date().toISOString())
  }, [fresh, addressUuid])

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-6 pt-12">
      <BatteryReport addressUuid={addressUuid} addressLabel="Voorbeeldstraat 1, 1234 AB Utrecht" />
    </div>
  )
}
